import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import haukeApiService from '../../services/haukeApi';
import '../../assets/css/profil-admin.css';

const Uebersicht = ({ hofId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mailing, setMailing] = useState(false);

  useEffect(() => {
    loadData();
  }, [hofId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await haukeApiService.getDirekterzeugerDetails(hofId);
      setData(response.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setData({ ...data, [field]: value });
    setHasChanges(true);
  };

  const toggleEdit = (field) => {
    setEditing(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const showSuccessToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'success-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 3000);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      const updateData = {
        name: data.name,
        street: data.street,
        plz: data.plz,
        city: data.city,
        tel: data.tel,
        mobile: data.mobile,
        mail: data.mail,
        membership: data.membership
      };
      
      await haukeApiService.updateDirekterzeuger(hofId, updateData);
      setHasChanges(false);
      setEditing({});
      showSuccessToast('Daten erfolgreich gespeichert!');
    } catch (err) {
      console.error('Error saving:', err);
      alert('Fehler beim Speichern: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Änderungen verwerfen?')) {
        loadData();
        setHasChanges(false);
        setEditing({});
      }
    }
  };

  const handleDelete = async () => {
    const hofName = data?.name || 'diesen Direkterzeuger';
    
    if (window.confirm(`Möchten Sie "${hofName}" (ID: ${hofId}) wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden!`)) {
      try {
        setDeleting(true);
        await haukeApiService.deleteHof(hofId);
        showSuccessToast(`${hofName} wurde erfolgreich gelöscht.`);
        
        // Nach erfolgreichem Löschen zur Direkterzeuger-Liste navigieren
        setTimeout(() => {
          navigate('/direkterzeuger-admin');
        }, 2000);
        
      } catch (err) {
        console.error('Error deleting direkterzeuger:', err);
        alert(`Fehler beim Löschen: ${err.message}`);
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleSendCommunityMail = async () => {
    try {
      setMailing(true);
      const res = await haukeApiService.sendCommunityMail(hofId);
      showSuccessToast('Community-Mail ausgelöst');
      await loadData();
    } catch (err) {
      console.error('Error sending community mail:', err);
      alert('Fehler beim Auslösen der Community-Mail: ' + err.message);
    } finally {
      setMailing(false);
    }
  };

  const getMembershipBadge = (membership) => {
    if (!membership) return 'Nicht gesetzt';
    
    const badges = {
      basic: { label: 'Basic', className: 'membership-badge basic' },
      plus: { label: 'Plus', className: 'membership-badge plus' },
      premium: { label: 'Premium', className: 'membership-badge premium' }
    };
    
    const badge = badges[membership.toLowerCase()] || { label: membership, className: 'membership-badge' };
    return <span className={badge.className}>{badge.label}</span>;
  };

  const formatLastChanged = (ts) => {
    if (!ts) return 'Nicht verfügbar';
    let date = null;
    if (typeof ts === 'number') {
      date = new Date(ts);
    } else if (typeof ts === 'string') {
      date = new Date(ts);
    } else if (ts && typeof ts === 'object') {
      if (typeof ts.seconds === 'number') date = new Date(ts.seconds * 1000);
      else if (typeof ts._seconds === 'number') date = new Date(ts._seconds * 1000);
      else if (typeof ts.toDate === 'function') date = ts.toDate();
    }
    if (!date || isNaN(date.getTime())) return 'Nicht verfügbar';
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const EditableField = ({ field, value, type = 'text', options = null }) => {
    const isEditing = editing[field];
    const displayValue = value || '';

    return (
      <div className="editable-field">
        {isEditing ? (
          <div className="edit-input">
            {options ? (
            <select
              value={displayValue}
              onChange={(e) => handleInputChange(field, e.target.value)}
              disabled={saving}
                onBlur={() => toggleEdit(field)}
                autoFocus
            >
              <option value="">Bitte wählen...</option>
              {options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          ) : (
            <input
              type={type}
              value={displayValue}
              onChange={(e) => handleInputChange(field, e.target.value)}
              disabled={saving}
                onBlur={() => toggleEdit(field)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') toggleEdit(field);
                  if (e.key === 'Escape') { 
                    loadData(); 
                    setHasChanges(false);
                    toggleEdit(field);
                  }
                }}
                autoFocus
            />
          )}
          </div>
        ) : (
          <div className="display-value" onClick={() => toggleEdit(field)}>
            {displayValue || 'Nicht gesetzt'}
            <i className="material-icons edit-icon">edit</i>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="webling-loading">Daten werden geladen...</div>;
  if (error) return <div className="webling-error">{error}</div>;
  if (!data) return <div className="webling-error">Keine Daten verfügbar</div>;

  return (
    <div className="uebersicht-container">
      <div className="uebersicht-content">
        <div className="overview-section">
          <h3>Direkterzeuger-Übersicht</h3>
          
          <div className="overview-boxes">
            {/* Linke Box - Bearbeitbare Daten */}
            <div className="overview-box left-box">
              <div className="box-content">
                <div className="overview-field">
                  <label>Name</label>
                  <EditableField field="name" value={data.name} />
        </div>

                <div className="overview-field">
                  <label>Adresse</label>
                  <EditableField field="street" value={data.street} />
                </div>
                
                <div className="overview-field-row">
                  <div className="overview-field">
                    <label>PLZ</label>
                    <EditableField field="plz" value={data.plz} type="number" />
                  </div>
                  <div className="overview-field">
                    <label>Stadt</label>
                    <EditableField field="city" value={data.city} />
                  </div>
                </div>
                
                <div className="overview-field-row">
                  <div className="overview-field">
                    <label>Telefon</label>
                    <EditableField field="tel" value={data.tel} type="tel" />
                  </div>
                  <div className="overview-field">
                    <label>Mobil</label>
                    <EditableField field="mobile" value={data.mobile} type="tel" />
                  </div>
                </div>
                
                <div className="overview-field">
                  <label>E-Mail</label>
                  <EditableField field="mail" value={data.mail} type="email" />
                </div>
              </div>
            </div>

            {/* Rechte Box - Technische Daten */}
            <div className="overview-box right-box">
              <div className="box-content">
                <div className="overview-field">
                  <label>Hof-ID</label>
                  <div className="readonly-value">
                    <code className="hof-id-display">{hofId}</code>
                  </div>
                </div>
                
                <div className="overview-field">
                  <label>GeoHash</label>
                  <div className="readonly-value">
                    <code className="geohash-display">{data.geohash || 'Nicht verfügbar'}</code>
                  </div>
        </div>

                <div className="overview-field">
                  <label>Koordinaten</label>
                  <div className="readonly-value">
                    <code className="coordinates-display">
                      {data.latitude && data.longitude 
                        ? `${data.latitude}, ${data.longitude}`
                        : 'Nicht verfügbar'
                      }
                    </code>
                  </div>
                </div>
                
                <div className="overview-field">
                  <label>Mitgliedschaft</label>
                  <div className="membership-field">
                    <EditableField 
                      field="membership" 
                      value={data.membership} 
                      options={['basic', 'plus', 'premium']}
                    />
                    <div className="membership-display">
                      {getMembershipBadge(data.membership)}
                    </div>
                  </div>
                </div>
                
                <div className="overview-field">
                  <label>Letzte Änderung</label>
                  <div className="readonly-value">
                    <span className="timestamp">
                      {formatLastChanged(data.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {hasChanges && (
        <div className="overview-actions">
        <button 
          onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
        >
            <i className="material-icons">save</i>
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
        <button 
          onClick={handleCancel}
          disabled={saving}
            className="btn btn-outline"
        >
            <i className="material-icons">close</i>
            Abbrechen
        </button>
      </div>
      )}

      {/* Community Mail – eigener Bereich */}
      <div className="overview-actions-inline" style={{ marginTop: 16, gap: 12, display: 'flex', flexWrap: 'wrap' }}>
        {(() => { const sent = !!data?.communityProductsWelcomeSent; return (
        <button
          onClick={handleSendCommunityMail}
          disabled={mailing || saving || deleting}
          className="btn btn-secondary"
          style={!sent ? { background:'#165f2f', borderColor:'#165f2f', color:'#fff' } : undefined}
        >
          <i className="material-icons">mail</i>
          {mailing ? 'Sende...' : 'Community Mail'}
        </button>
        ); })()}
        <span className="status-inline" style={{ alignSelf: 'center', color: '#6b7280' }}>
          {data?.communityProductsWelcomeSent ? 'Gesendet' : 'Nicht gesendet'}
          {data?.communityProductsWelcomeSent_date ? ` (${formatLastChanged(data.communityProductsWelcomeSent_date)})` : ''}
        </span>
        {(() => { const sent = !!data?.welcomemail_sent; return (
        <button
          onClick={async () => { try { setMailing(true); await haukeApiService.sendWelcomeMail(hofId); showSuccessToast('Welcome-Mail ausgelöst'); await loadData(); } catch (e) { alert('Fehler: ' + (e?.message||'Unbekannt')); } finally { setMailing(false); } }}
          disabled={mailing || saving || deleting}
          className="btn btn-secondary"
          style={!sent ? { background:'#165f2f', borderColor:'#165f2f', color:'#fff' } : undefined}
        >
          <i className="material-icons">mail_outline</i>
          {mailing ? 'Sende...' : 'Welcome Mail'}
        </button>
        ); })()}
        <span className="status-inline" style={{ alignSelf: 'center', color: '#6b7280' }}>
          {data?.welcomemail_sent ? 'Gesendet' : 'Nicht gesendet'}
          {data?.welcomemail_sentdate ? ` (${formatLastChanged(data.welcomemail_sentdate)})` : ''}
        </span>
      </div>

      {/* Delete Section – ganz unten */}
      <div className="overview-delete-section" style={{ marginTop: 24 }}>
        <div className="delete-warning">
          <i className="material-icons">warning</i>
          <span>Achtung: Das Löschen kann nicht rückgängig gemacht werden!</span>
        </div>
        <button 
          onClick={handleDelete}
          disabled={deleting || saving}
          className="btn btn-danger"
        >
          <i className="material-icons">delete_forever</i>
          {deleting ? 'Lösche...' : 'Direkterzeuger löschen'}
        </button>
      </div>
    </div>
  );
};

export default Uebersicht; 